<template>
  <div class="multisteps-form__panel border-radius-xl bg-white" data-animation="FadeIn">
    <h5 class="font-weight-bolder mb-0">{{ $t('location.Location_info') }}</h5>
    <p class="mb-0 text-sm">{{ $t('location.Mandatory_informations') }} *</p>
    <div class="multisteps-form__content">
      <div class="row mt-3">
          <div class="col-6">
            <div class="input-group input-group-static null">
              <label class="">{{ $t('location.UBN') }} *</label>
              <input v-model="localFormData.ubn" type="text" class="form-control form-control-default" :placeholder="$t('location.UBN')">
              <span v-if="v$.ubn.$error" class="text-sm text-danger">
                {{ v$.ubn.$errors[0].$message }}
              </span>
            </div>
          </div>
          <div class="col-6">
            <div class="input-group input-group-static null">
              <label class="">{{ $t('location.Name') }}</label>
              <input v-model="localFormData.name" type="text" class="form-control form-control-default" :placeholder="$t('location.Name')">
            </div>
          </div>
      </div>
      <div class="row mt-4">
        <div class="col-6">
          <div class="input-group input-group-static null">
              <label class="">{{ $t('location.Location_type') }} *</label>
              <select id="locationTypeId" v-model="localFormData.locationTypeId" class="form-control form-control-default">
                <option value="">{{ $t('location.Select_location_type') }}</option>
                <option v-for="locationType in localFormData.locationTypes" :key="locationType.id" :value="locationType.id">
                  {{ locationType.name }}
                </option>
            </select>              
            <span v-if="v$.locationTypeId.$error" class="text-sm text-danger">
              {{ v$.locationTypeId.$errors[0].$message }}
            </span>
          </div>
        </div>
        <div class="col-6">
          <div class="input-group input-group-static null">
            <label class="">{{ $t('location.Keeper') }} *</label>
            <select id="keeperId" v-model="localFormData.keeperId" class="form-control form-control-default">
                <option value="">{{ $t('location.Select_keeper') }}</option>
                <option v-for="keeper in localFormData.keepers" :key="keeper.id" :value="keeper.id">
                  {{ keeper.fullname }}
                </option>
            </select>
            <span v-if="v$.keeperId.$error" class="text-sm text-danger">
                {{ v$.keeperId.$errors[0].$message }}
            </span>
          </div>
        </div>
      </div>
      <div class="row mt-4">        
        <div class="col-6">
          <div class="input-group input-group-static null">
              <label class="">{{ $t('location.Animal_type') }} *</label>
              <select id="animalTypeId" v-model="localFormData.animalTypeId" class="form-control form-control-default custom-choices" multiple>
                <option value="">{{ $t('location.Select_animaltype') }}</option>
                <option v-for="item in localFormData.animalTypes" :key="item.id" :value="item.id">
                  {{ item.name }}
                </option>
            </select>              
            <span v-if="v$.animalTypeId.$error" class="text-sm text-danger">
              {{ v$.animalTypeId.$errors[0].$message }}
            </span>
          </div>
        </div>
        <div class="col-6">
          <div class="input-group input-group-static null" style="width: 200px;">
              <label class="">{{ $t('location.Max_animal_capicity') }}</label>
              <input v-model="localFormData.maxAnimalCapacity" type="text" class="form-control form-control-default" :placeholder="$t('location.Max_animal_capicity')">   
              <span v-if="v$.maxAnimalCapacity.$error" class="text-sm text-danger">
                {{ v$.maxAnimalCapacity.$errors[0].$message }}
              </span>
          </div>
        </div>
      </div>      
      <div class="button-row d-flex mt-4">
        <button class="btn mb-0 btn-sm bg-gradient-success null null ms-auto mb-0 js-btn-next" type="button" @click="validateAndNextStep">{{ $t('common.Next') }}</button>        
      </div>
    </div>
  </div>
</template>
<script>
import axios from 'axios'
import * as Choices from "choices.js";
import useVuelidate from '@vuelidate/core'
import { required, helpers } from '@vuelidate/validators'
import { computed, reactive, watch } from 'vue'
import { useI18n } from 'vue-i18n'

export default {
  name: "LocationInfo",
  props: {
    formData: Object,
    nextStep: Function
  },  
  components: {          
  },
  setup(props, { emit }) {
    const { t } = useI18n()

    const localFormData = reactive({ ...props.formData })

    const integerOnly = helpers.withMessage(
      t('common.Integer_only'),
      value => {
        if (value === '' || value === null || value === undefined) return true; // allow empty
        return /^[0-9]+$/.test(value); // only allow integers
      }
    )

    const rules = computed(() => ({
      ubn: { required: helpers.withMessage(t('common.Required'), required) },      
      locationTypeId: { 
          required: helpers.withMessage(t('common.Required'), required),
          isSelected: helpers.withMessage(t('location.Select_a_locationtype'), value => value !== '')
      },
      keeperId: { 
          required: helpers.withMessage(t('common.Required'), required),
          isSelected: helpers.withMessage(t('location.Select_a_keeper'), value => value !== '')
      },
      animalTypeId: { 
          required: helpers.withMessage(t('common.Required'), required),
          isSelected: helpers.withMessage(t('location.Select_animaltype'), value => value !== '')
      },
      maxAnimalCapacity: { integerOnly }
    }))
    
    const v$ = useVuelidate(rules, localFormData)
        
    watch(localFormData, () => {
      emit('update:formData', localFormData)
    }, { deep: true })

    const validateAndNextStep = async () => {
      v$.value.$touch() // Mark all fields as touched
      if (v$.value.$pending) {
        await v$.value.$validate() // Ensure validation completes
      }
      if (!v$.value.$invalid) {
        props.nextStep()
      }
    }

    return {
      localFormData,      
      v$,
      validateAndNextStep
    }
  },
  async mounted() {      
    
    this.$store.commit('setShowLoading', true);

    await this.fetchAllLocationTypeData();
    await this.fetchAllKeeperData();
    await this.fetchAllAnimalTypeData();

    const element = document.getElementById("animalTypeId");
    element.addEventListener("change", () => {
      const selected = Array.from(element.selectedOptions).map(opt => opt.value);
      this.localFormData.animalTypeId = selected;
    });

    this.$store.commit('setShowLoading', false);
  },
  methods: {    
    async fetchAllLocationTypeData() {
      try {         
          
          
          const response = await axios.get("reference/getreference?referenceTypeId=5977db02-0ac6-4dc7-ba44-edb5b0ab786e"); //Locationtype
          const result = response.data;

          if (result.success && Array.isArray(result.data)) {
              this.localFormData.locationTypes = result.data;                        
              // Wait for DOM to update
              this.$nextTick(() => {
                  this.initializeChoicesLocationType(); // Initialize Choices here
              });
          } else {
              this.$showSuccess('errors.Error_fetching_locationtypes');
          }
      } catch (e) {
          const errorMessage = e.response?.data?.message || e.message || this.$t('errors.Something_went_wrong');
          this.$showError(`${this.$t('errors.Contact_system_admin')}: ${errorMessage}`);
      }
    },
    initializeChoicesLocationType() {            
        const element = document.getElementById("locationTypeId");
        if (this.locationTypeChoicesInstance) {
            this.locationTypeChoicesInstance.destroy(); // Clean up previous instance
        }

        this.locationTypeChoicesInstance = new Choices(element, {
            searchEnabled: true,
            itemSelectText: '',
            shouldSort: false,
            placeholder: true, 
            //placeholderValue: '-- Select location type --', 
        });      

        if (this.localFormData.locationTypeId) {
            const optionExists = this.localFormData.locationTypes.some(locationType => locationType.id === this.localFormData.locationTypeId);
            if (optionExists) {
                this.locationTypeChoicesInstance.setChoiceByValue([this.localFormData.locationTypeId]); // Set the selected value
            } else {
                this.locationTypeChoicesInstance.setChoiceByValue(''); // Clear if no match found
            }
        }
    },
    async fetchAllKeeperData() {
        try {
            const response = await axios.get("user/getuser?role=keeper");
            const result = response.data;

            if (result.success && Array.isArray(result.data.userList)) {                        
                this.localFormData.keepers = result.data.userList;

                // Wait for DOM to update
                this.$nextTick(() => {
                    this.initializeChoicesKeeper(); // Initialize Choices here
                });
            } else {
              this.$showError('errors.Error_fetching_keeper');
            }
        } catch (e) {                    
            const errorMessage = e.response?.data?.message || e.message || this.$t('errors.Something_went_wrong');
            this.$showError(`${this.$t('errors.Contact_system_admin')}: ${errorMessage}`);
        }
    },
    initializeChoicesKeeper() {                    
        const element = document.getElementById("keeperId");
        if (this.keeperChoicesInstance) {
            this.keeperChoicesInstance.destroy(); // Clean up previous instance                    
        }
        
        this.keeperChoicesInstance  = new Choices(element, {
            searchEnabled: true,
            itemSelectText: '',
            shouldSort: false,
            placeholder: true, 
            //placeholderValue: '-- Select keeper --', 
        });      

        if (this.localFormData.keeperId) {
            const optionExists = this.localFormData.keepers.some(keeper => keeper.id === this.localFormData.keeperId);
            if (optionExists) {
                this.keeperChoicesInstance.setChoiceByValue([this.localFormData.keeperId]); // Set the selected value
            } else {
                this.keeperChoicesInstance.setChoiceByValue(''); // Clear if no match found
            }
        }
    },
    async fetchAllAnimalTypeData() {
        try {
            const response = await axios.get("reference/getreference?referenceTypeId=b81c5e43-3e52-49b4-9205-57330f75c3d1"); //AnimalType
            const result = response.data;

            if (result.success && Array.isArray(result.data)) {
                this.localFormData.animalTypes = result.data;

                // Wait for DOM to update
                this.$nextTick(() => {
                    this.initializeChoicesAnimalTypes(); // Initialize Choices here
                });
            } else {
              this.$showError('errors.Error_fetching_animalTypes');
            }
        } catch (e) {                    
            const errorMessage = e.response?.data?.message || e.message || this.$t('errors.Something_went_wrong');
            this.$showError(`${this.$t('errors.Contact_system_admin')}: ${errorMessage}`);
        }
    },
    initializeChoicesAnimalTypes() {                    
        const element = document.getElementById("animalTypeId");
        if (this.animalTypesChoicesInstance) {
            this.animalTypesChoicesInstance.destroy(); // Clean up previous instance                    
        }
        
        this.animalTypesChoicesInstance  = new Choices(element, {
          removeItemButton: true,
          searchEnabled: true,
          itemSelectText: '',
          shouldSort: false,
          placeholder: true,  
          //placeholderValue: '-- Select animalTypes --', 
        });      

        // Sync selected values if already chosen
        if (Array.isArray(this.localFormData.animalTypeId)) {
          const validValues = this.localFormData.animalTypeId.filter(id =>
            this.localFormData.animalTypes.some(item => item.id === id)
          );

          this.animalTypesChoicesInstance.setChoiceByValue(validValues);
        }
    },
  }

}
</script>
<style scoped>
.d-block {
  display: block !important;
}
.choices__inner {
  display: grid !important;  
}

</style>